import { z } from "zod";

import { recordingStatusSchema } from "@/custolink-views/shared/recording_consent/recordingStatus";

/**
 * envelope - the one typed, versioned, size-bounded LiveKit data-channel contract (D-01).
 *
 * Wire format (every topic except cl.frame): UTF-8 JSON `{ "v": SCHEMA_VERSION, "t": "<type>", "p": <payload> }`.
 * decodeEnvelope never throws: an unknown topic, an oversized message, invalid UTF-8/JSON, a wrong
 * schema version, an unknown or wrong-topic message type, or a payload that fails validation are all
 * dropped and logged (topic + reason only, never the payload) - the caller always gets a plain
 * `{ ok: false, reason }` value back, never an exception. cl.frame carries raw JPEG bytes via
 * LiveKit's streamBytes and has no JSON envelope; encodeEnvelope refuses it (topic reason) because
 * frame bytes are sent directly by the caller, not wrapped here.
 */

export const SCHEMA_VERSION = 1;

export const TOPICS = {
  pointer: "cl.pointer",
  op: "cl.op",
  state: "cl.state",
  frame: "cl.frame",
} as const;

export type SyncTopic = (typeof TOPICS)[keyof typeof TOPICS];

// LiveKit reliable data packets carry about 15 KiB of usable payload; lossy packets must stay well
// under typical network MTU to avoid fragmentation drops. 15000 matches the backend guard
// LivekitRoomService.MAX_RELIABLE_PAYLOAD_BYTES exactly, so client and server agree on the limit.
export const MAX_BYTES = {
  "cl.pointer": 1300,
  "cl.op": 15000,
  "cl.state": 15000,
} as const;

export const MAX_FRAME_BYTES = 4 * 1024 * 1024;

const pointerSourceSchema = z.object({
  kind: z.union([z.literal("track"), z.literal("frame")]),
  id: z.string().min(1).max(100),
});

export const pointerPayloadSchema = z.object({
  src: pointerSourceSchema,
  x: z.number().min(0).max(1),
  y: z.number().min(0).max(1),
});
export type PointerPayload = z.infer<typeof pointerPayloadSchema>;

const pointerHidePayloadSchema = z.object({}).strict();

export const opMessagePayloadSchema = z.object({
  opId: z.string().uuid(),
  frameId: z.string().uuid(),
  kind: z.union([
    z.literal("ShapeAdd"),
    z.literal("ShapeUpdate"),
    z.literal("ShapeDelete"),
    z.literal("ShapeRestore"),
  ]),
  payload: z.record(z.unknown()),
  undoOf: z.string().uuid().nullable(),
  redoOf: z.string().uuid().nullable(),
});
export type OpMessagePayload = z.infer<typeof opMessagePayloadSchema>;

const rotationSchema = z.union([z.literal(0), z.literal(90), z.literal(180), z.literal(270)]);
const positiveIntUpTo8192 = z.number().int().positive().max(8192);

export const frameDescriptorSchema = z.object({
  frameId: z.string().uuid(),
  sourceId: z.string().min(1),
  sourceIdentity: z.string().min(1),
  sourceW: positiveIntUpTo8192,
  sourceH: positiveIntUpTo8192,
  mirrored: z.boolean(),
  rotation: rotationSchema,
  capturedAtEpochMs: z.number().int().nonnegative(),
});
export type SyncFrameDescriptorPayload = z.infer<typeof frameDescriptorSchema>;

export const opViewSchema = z.object({
  opId: z.string().uuid(),
  seq: z.number().int().nonnegative(),
  frameId: z.string().uuid(),
  authorIdentity: z.string().min(1),
  kind: z.string().min(1),
  payload: z.string(),
  undoOf: z.string().uuid().nullable(),
  redoOf: z.string().uuid().nullable(),
  createdAtEpochMs: z.number().int().nonnegative(),
});
export type SyncOpViewPayload = z.infer<typeof opViewSchema>;

export const roomStatePayloadSchema = z.object({
  stateVersion: z.number().int().nonnegative(),
  latestSeq: z.number().int().nonnegative(),
  customerMayAnnotate: z.boolean(),
  activeSource: z
    .object({
      trackSid: z.string().min(1),
      identity: z.string().min(1),
    })
    .nullable(),
  activeFrame: frameDescriptorSchema.nullable(),
  freezeRequest: z
    .object({
      frameId: z.string().uuid(),
      trackSid: z.string().min(1),
      identity: z.string().min(1),
      requestedAtEpochMs: z.number().int().nonnegative(),
    })
    .nullable(),
  ops: z.array(opViewSchema),
  hasMoreOps: z.boolean(),
  // D-09: the server's recording state rides the same room-state message. Optional so an older
  // server that does not send it still decodes; unknown keys are stripped, so it must be named.
  recording: recordingStatusSchema.optional(),
});
export type RoomStatePayload = z.infer<typeof roomStatePayloadSchema>;

// topic -> allowed type -> payload schema. cl.frame has no JSON envelope and is not listed here.
const TOPIC_TYPE_SCHEMAS: Record<string, Record<string, z.ZodTypeAny>> = {
  [TOPICS.pointer]: {
    pointer: pointerPayloadSchema,
    "pointer-hide": pointerHidePayloadSchema,
  },
  [TOPICS.op]: {
    op: opMessagePayloadSchema,
  },
  [TOPICS.state]: {
    "room-state": roomStatePayloadSchema,
  },
};

export type DecodeResult =
  | { ok: true; type: string; payload: unknown }
  | { ok: false; reason: "topic" | "oversize" | "json" | "version" | "type" | "schema" };

export type EnvelopeLogger = (topic: string | undefined, reason: string) => void;

function defaultLog(topic: string | undefined, reason: string): void {
  console.warn(`[remote-support-sync] dropped envelope on topic ${String(topic)}: ${reason}`);
}

export function encodeEnvelope(
  topic: string,
  type: string,
  payload: unknown,
  log: EnvelopeLogger = defaultLog,
): Uint8Array | null {
  const typeSchemas = TOPIC_TYPE_SCHEMAS[topic];
  if (!typeSchemas) {
    log(topic, "topic");
    return null;
  }
  const maxBytes = (MAX_BYTES as Record<string, number>)[topic];
  const bytes = new TextEncoder().encode(JSON.stringify({ v: SCHEMA_VERSION, t: type, p: payload }));
  if (maxBytes === undefined || bytes.byteLength > maxBytes) {
    log(topic, "oversize");
    return null;
  }
  return bytes;
}

export function decodeEnvelope(
  topic: string | undefined,
  bytes: Uint8Array,
  log: EnvelopeLogger = defaultLog,
): DecodeResult {
  if (!topic || !(topic in TOPIC_TYPE_SCHEMAS)) {
    log(topic, "topic");
    return { ok: false, reason: "topic" };
  }

  const maxBytes = (MAX_BYTES as Record<string, number>)[topic];
  if (maxBytes === undefined || bytes.byteLength > maxBytes) {
    log(topic, "oversize");
    return { ok: false, reason: "oversize" };
  }

  let text: string;
  try {
    text = new TextDecoder("utf-8", { fatal: true }).decode(bytes);
  } catch {
    log(topic, "json");
    return { ok: false, reason: "json" };
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    log(topic, "json");
    return { ok: false, reason: "json" };
  }

  if (typeof parsed !== "object" || parsed === null) {
    log(topic, "json");
    return { ok: false, reason: "json" };
  }

  const envelope = parsed as { v?: unknown; t?: unknown; p?: unknown };
  if (envelope.v !== SCHEMA_VERSION) {
    log(topic, "version");
    return { ok: false, reason: "version" };
  }

  if (typeof envelope.t !== "string") {
    log(topic, "type");
    return { ok: false, reason: "type" };
  }

  const schema = TOPIC_TYPE_SCHEMAS[topic][envelope.t];
  if (!schema) {
    log(topic, "type");
    return { ok: false, reason: "type" };
  }

  const result = schema.safeParse(envelope.p);
  if (!result.success) {
    log(topic, "schema");
    return { ok: false, reason: "schema" };
  }

  return { ok: true, type: envelope.t, payload: result.data };
}

export function roleOfIdentity(identity: string): "worker" | "customer" | "unknown" {
  if (identity.startsWith("worker-")) {
    return "worker";
  }
  if (identity.startsWith("enduser-")) {
    return "customer";
  }
  return "unknown";
}

export function acceptSender(topic: string | undefined, senderIdentity: string | null): boolean {
  if (topic === TOPICS.state) {
    return senderIdentity === null;
  }
  if (topic === TOPICS.op || topic === TOPICS.pointer) {
    if (senderIdentity === null) {
      return false;
    }
    const role = roleOfIdentity(senderIdentity);
    return role === "worker" || role === "customer";
  }
  return false;
}
